import { z } from "zod";
import { getIndexAbove } from "@tldraw/utils";
import type {
  TLAssetId,
  TLShapeId,
  TLParentId,
  TLImageAsset,
  TLShape,
} from "@tldraw/tlschema";
import type { OperationContext } from "./agent-operation-context";
const shapeId = z.string().regex(/^shape:[\w-]{1,100}$/),
  assetId = z.string().regex(/^asset:[\w-]{1,100}$/);
export const sourceProvenance = z
  .object({
    sourceId: z.string().min(1).max(200),
    url: z.string().url().startsWith("https://").max(4000).optional(),
    filePath: z.string().max(4000).optional(),
    prompt: z.string().max(4000).optional(),
    generationId: z.string().max(200).optional(),
    revision: z.string().max(200).optional(),
  })
  .strict();
const file = z
  .object({
    assetId,
    src: z.string().regex(/^\/api\/rooms\/[\w-]+\/files\/[\w-]+$/),
    pixelWidth: z.number().int().min(1).max(40000),
    pixelHeight: z.number().int().min(1).max(40000),
    mimeType: z.enum(["image/png", "image/jpeg", "image/webp"]),
    name: z.string().min(1).max(200),
  })
  .strict();
export const imageOperations = [
  z
    .object({
      type: z.literal("place_image"),
      id: shapeId,
      assetId,
      parentId: z.string().optional(),
      sheetId: shapeId.optional(),
      pageId: z
        .string()
        .regex(/^page:[\w-]+$/)
        .optional(),
      x: z.number().finite(),
      y: z.number().finite(),
      w: z.number().positive().max(10000),
      h: z.number().positive().max(10000).optional(),
      provenance: sourceProvenance.optional(),
    })
    .strict(),
  z
    .object({
      type: z.literal("replace_image"),
      id: shapeId,
      assetId: assetId.optional(),
      file: file.optional(),
      fit: z.enum(["contain", "cover"]).default("contain"),
      provenance: sourceProvenance.optional(),
      reviewCalibration: z.boolean().default(false),
    })
    .strict(),
  z
    .object({
      type: z.literal("set_image_source"),
      id: shapeId,
      provenance: sourceProvenance,
    })
    .strict(),
] as const;
const operation = z.discriminatedUnion("type", imageOperations);
export function applyImageOperation(input: unknown, c: OperationContext) {
  const parsed = operation.safeParse(input);
  if (!parsed.success) return false;
  const op = parsed.data;
  if (op.type === "set_image_source") {
    const shape = c.editable(op.id);
    if (shape.type !== "image") throw new Error("Choose an image");
    c.set({ ...shape, meta: { ...shape.meta, imageSource: op.provenance } });
    return true;
  }
  let id = op.assetId;
  if (op.type === "replace_image" && op.file) {
    if (id)
      throw new Error("Choose assetId or a new file descriptor, not both");
    id = op.file.assetId;
    const existing = c.txn.get(id);
    if (existing) throw new Error("Use assetId to reuse an existing asset");
    c.set({
      id: id as TLAssetId,
      typeName: "asset",
      type: "image",
      meta: { imagePreviews: true },
      props: {
        src: op.file.src,
        w: op.file.pixelWidth,
        h: op.file.pixelHeight,
        mimeType: op.file.mimeType,
        name: op.file.name,
        isAnimated: false,
      },
    });
  }
  const asset = id ? c.txn.get(id) : undefined;
  if (!asset || asset.typeName !== "asset" || asset.type !== "image")
    throw new Error(
      "Choose a registered image asset or provide a new private file descriptor",
    );
  if (op.type === "place_image") {
    if (c.txn.get(op.id)) throw new Error("Shape ID already exists");
    if (Boolean(op.sheetId) === Boolean(op.pageId))
      throw new Error("Choose exactly one sheet or page");
    const parent = op.sheetId ? c.editable(op.sheetId) : c.txn.get(op.pageId!);
    if (
      !parent ||
      (parent.typeName !== "page" && parent.typeName !== "shape") ||
      (op.sheetId && (parent.typeName !== "shape" || parent.type !== "sheet"))
    )
      throw new Error("Choose an existing sheet or page");
    const siblings = [...c.txn.values()].filter(
      (r): r is TLShape => r.typeName === "shape" && r.parentId === parent.id,
    );
    c.set({
      id: op.id as TLShapeId,
      typeName: "shape",
      type: "image",
      parentId: parent.id as TLParentId,
      x: op.x,
      y: op.y,
      index: getIndexAbove(
        siblings
          .map((r) => r.index)
          .sort()
          .at(-1),
      ),
      rotation: 0,
      opacity: 1,
      isLocked: false,
      meta: { ...(op.provenance ? { imageSource: op.provenance } : {}) },
      props: {
        w: op.w,
        h: op.h ?? (op.w * asset.props.h) / asset.props.w,
        assetId: asset.id,
        playing: true,
        url: "",
        crop: null,
        flipX: false,
        flipY: false,
        altText: asset.props.name,
      },
    });
    return true;
  }
  const shape = c.editable(op.id);
  if (shape.type !== "image")
    throw new Error("Choose an image object to replace");
  const calibrated = Number(shape.meta.metersPerUnit) > 0;
  if (calibrated && !op.reviewCalibration)
    throw new Error(
      "Set reviewCalibration:true to replace a calibrated source and reset its scale for review",
    );
  const ratio = asset.props.w / asset.props.h,
    box = shape.props.w / shape.props.h;
  const cw = ratio > box ? box / ratio : 1,
    ch = ratio > box ? 1 : ratio / box;
  const crop =
    op.fit === "cover"
      ? {
          topLeft: { x: (1 - cw) / 2, y: (1 - ch) / 2 },
          bottomRight: { x: (1 + cw) / 2, y: (1 + ch) / 2 },
        }
      : null;
  c.set({
    ...shape,
    props: { ...shape.props, assetId: asset.id, crop },
    meta: {
      ...shape.meta,
      imageFit: op.fit,
      imageSource: op.provenance ?? { sourceId: asset.id },
      ...(calibrated
        ? {
            metersPerUnit: 0,
            calibrationWidth: asset.props.w,
            sourceAlignmentNeedsReview: true,
          }
        : {}),
    },
  });
  return true;
}
